import { AlpineData, LivewireData, LivewireMethods } from '@/js/interfaces/Components/alpinejs/BpuUpdate/BpuImportedItems';
import is from "@/js/app/support/is";
import { InvalidArgumentException } from "@/js/app/exceptions";
import { createAlpineLivewireComponent } from '@nolikein/typed-alpinejs';

export default {
    name: 'BpuImportedItems',
    component: () => createAlpineLivewireComponent<AlpineData, LivewireData, LivewireMethods>({
        importedItems: [],
        mayBeDeletedItems: [],
        display: false,
        selection: {
            update: new Map(),
            delete: new Map(),
        },
        isUpdatingBpu: false,

        /**
         * Actions to perform after the document has been imported
         */
        onTreated(e: CustomEvent): void {
            if (!is.array(e.detail.importedItems)) {
                throw new InvalidArgumentException('The "importedItems" key do not return an array');
            }
            this.importedItems = e.detail.importedItems;

            if (!is.array(e.detail.mayBeDeletedItems)) {
                throw new InvalidArgumentException('The "mayBeDeletedItems" key do not return an array');
            }
            this.mayBeDeletedItems = e.detail.mayBeDeletedItems;

            // Display the pannel now that all data appeared
            this.display = true;

            // Set updated items as selected by default
            this.setAllForUpdate();
        },

        /**
         * Toggle an item to update
         */
        toggleItemToUpdate(codeLine: string): void {
            if (this.selection.update.has(codeLine)) {
                this.selection.update.delete(codeLine);
            } else {
                const item = this.importedItems.find(i => i.code_line === codeLine);
                if (undefined !== item) {
                    this.selection.update.set(codeLine, item);
                }
            }
        },

        /**
         * Toggle and item to delete
         */
        toggleItemToDelete(id: number): void {
            if (this.selection.delete.has(id)) {
                this.selection.delete.delete(id);
            } else {
                const item = this.mayBeDeletedItems.find(i => i.id === id);
                if (undefined !== item) {
                    this.selection.delete.set(id, item);
                }
            }
        },

        /**
         * Actions to perform after the user has been asked to update the bpu and he confirmed his action
         */
        onConfirmUpdateBpu(): void {
            this.$wire.$dispatch('update-bpu', {
                updatedItems: Array.from(this.selection.update, ([name, value]) => ({ codeLine: name, item: value })),
                deletedItems: Array.from(this.selection.delete, ([name, value]) => ({ id: name, item: value })),
            });
            this.display = false;
            this.isUpdatingBpu = true;
        },

        /**
         * Actions to perform after the bpu has been updated
         */
        onUpdated() {
            this.isUpdatingBpu = false;
        },

        toggleAllUpdateSelection(): void {
            if (this.hasAnyUpdatingSelection()) {
                this.setNothingForUpdate();
            } else {
                this.setAllForUpdate();
            }
        },

        toggleAllTrashSelection(): void {
            if (this.hasAnyTrashSelection()) {
                this.setNothingForTrash();
            } else {
                this.setAllForTrash();
            }
        },

        setAllForUpdate(): void {
            this.selection.update.clear();
            this.importedItems.forEach(item => {
                this.selection.update.set(item.code_line, item);
            });
        },

        setNothingForUpdate(): void {
            this.selection.update.clear();
        },

        setAllForTrash(): void {
            this.selection.delete.clear();
            this.mayBeDeletedItems.forEach(item => {
                this.selection.delete.set(item.id, item);
            });
        },

        setNothingForTrash(): void {
            this.selection.delete.clear();
        },

        hasAnyUpdatingSelection(): boolean {
            return this.selection.update.size > 0;
        },

        hasAnyTrashSelection(): boolean {
            return this.selection.delete.size > 0;
        },
    }),
};
